import { apiRequest } from './client';

// Credit / international cards are paid through Razorpay; UPI, debit cards and net banking through Cashfree.
export const getPaymentConfig = (signal) => apiRequest('/api/payments/config', { signal });
export const createPayment = (payment, token) => apiRequest('/api/payments', { body: payment, token });
export const verifyPayment = (paymentId, body = {}) => apiRequest(`/api/payments/${paymentId}/verify`, { body });
export const getPayment = (paymentId) => apiRequest(`/api/payments/${paymentId}`);

const scripts = {};
const loadScript = (src) => {
  scripts[src] = scripts[src] || new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => {
      delete scripts[src];
      reject(new Error('The payment page could not be loaded. Check your connection and try again.'));
    };
    document.body.appendChild(script);
  });
  return scripts[src];
};

const cancelled = () => new Error('Payment was cancelled. No money was taken — you can pay again.');

async function payWithRazorpay(order, contact, method) {
  await loadScript('https://checkout.razorpay.com/v1/checkout.js');
  const response = await new Promise((resolve, reject) => {
    const checkout = new window.Razorpay({
      key: order.razorpay.keyId,
      order_id: order.razorpay.orderId,
      amount: order.razorpay.amount,
      currency: order.currency,
      name: 'Trawtel',
      description: 'Booking payment',
      prefill: { email: contact.email, contact: contact.phone },
      // Razorpay is used for cards only; UPI, debit cards and net banking go through Cashfree.
      config: {
        display: {
          blocks: { cards: { name: method === 'international' ? 'Pay by international card' : 'Pay by credit card', instruments: [{ method: 'card' }] } },
          sequence: ['block.cards'],
          preferences: { show_default_blocks: false },
        },
      },
      handler: resolve,
      modal: { ondismiss: () => reject(cancelled()) },
    });
    // A declined card stays in Razorpay's window so the customer can try again or close it.
    checkout.open();
  });
  return verifyPayment(order.paymentId, {
    razorpayPaymentId: response.razorpay_payment_id,
    razorpayOrderId: response.razorpay_order_id,
    razorpaySignature: response.razorpay_signature,
  });
}

async function payWithCashfree(order) {
  await loadScript('https://sdk.cashfree.com/js/v3/cashfree.js');
  const cashfree = window.Cashfree({ mode: order.cashfree.mode });
  const result = await cashfree.checkout({ paymentSessionId: order.cashfree.paymentSessionId, redirectTarget: '_modal' });
  // The server asks Cashfree for the order status either way, so a closed window after paying still counts.
  const verified = await verifyPayment(order.paymentId);
  if (verified.status !== 'Paid' && result?.error) {
    throw new Error(result.error.message ? `${result.error.message} No money was taken — you can pay again.` : cancelled().message);
  }
  return verified;
}

/**
 * Takes payment for `amount` (INR) with the chosen method and resolves with the paid payment's id,
 * which the product checkout needs. Throws with a message for the customer when not paid.
 */
export async function collectPayment({ module, method, amount, contact }, token) {
  const order = await createPayment({
    product: module,
    method,
    amount,
    currency: 'INR',
    email: contact.email.trim(),
    phone: contact.phone.trim(),
  }, token);
  const result = order.gateway === 'Razorpay' ? await payWithRazorpay(order, contact, method) : await payWithCashfree(order);
  if (result.status !== 'Paid') throw new Error(result.message || 'The payment was not completed. Please try again.');
  return result.paymentId;
}
